import { useId, useState } from "react";
import { PencilLine } from "lucide-react";
import type {
  ElementRole,
  ElementRoleRequest,
  ElementRoleResponse,
  PositionResponse,
  StripEntryResponse,
  StripResponse,
} from "@patent-studio/api-types";
import { Button, IconButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { HoverTip } from "@/components/ui/HoverTip";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Select } from "@/components/ui/select";
import { StatusPill } from "@/components/ui/StatusPill";
import { Textarea } from "@/components/ui/textarea";
import type { Tone } from "@/components/ui/tone";
import { refusalDetail } from "@/services/api-client";
import { type MappableElement, mapped } from "../positions/element-mapping";
import { ElementChip } from "../positions/ElementPicker";

/** How each role is named on the card, and the tone it is drawn in. */
const ROLE: Record<ElementRole, { label: string; tone: Tone }> = {
  essential: { label: "Essential", tone: "info" },
  retreat: { label: "Retreat", tone: "neutral" },
};

/** What a refusal says when the server never answered. */
const UNREACHED = "The request did not reach the server.";

const NOT_ASSESSED = {
  label: "Not assessed",
  reason:
    "Not assessed: the position has not been checked against the prior art as it now reads.",
};

/** The attorney's choice for one element, and the act that saves it or takes it back. */
export interface ElementMarking {
  /** The mark the attorney saved on this element, or none. */
  marked: ElementRoleResponse | undefined;
  /**
   * Saves a mark, or takes it back to the strip's reading with `null`. It
   * settles once the server has answered, and rejects on a refusal.
   */
  onMark: (mark: ElementRoleRequest | null) => Promise<unknown>;
  busy: boolean;
}

/** The two fields of a mark: the role, and the reason for it. */
function MarkFields({
  role,
  reason,
  onRole,
  onReason,
}: {
  role: ElementRole;
  reason: string;
  onRole: (role: ElementRole) => void;
  onReason: (reason: string) => void;
}) {
  const roleId = useId();
  const reasonId = useId();
  return (
    <>
      <div>
        <FieldLabel htmlFor={roleId} look="sentence">
          Role
        </FieldLabel>
        <Select
          id={roleId}
          size="roomy"
          value={role}
          onChange={(event) => onRole(event.target.value as ElementRole)}
        >
          {(Object.keys(ROLE) as ElementRole[]).map((value) => (
            <option key={value} value={value}>
              {ROLE[value].label}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <FieldLabel htmlFor={reasonId} look="sentence">
          Reason
        </FieldLabel>
        <Textarea
          id={reasonId}
          size="roomy"
          rows={3}
          value={reason}
          onChange={(event) => onReason(event.target.value)}
        />
      </div>
    </>
  );
}

/**
 * The choice inside an element's mark: essential or retreat, with a reason.
 * It always saves; a check against it shows as a finding on the card. It
 * closes once the server has taken the mark; a refusal is said inside it, and
 * the role and the reason stay as typed.
 */
function MarkDialog({
  number,
  current,
  marking,
  onClose,
}: {
  number: number;
  current: ElementRole;
  marking: ElementMarking;
  onClose: () => void;
}) {
  const sentenceId = useId();
  const [role, setRole] = useState<ElementRole>(
    marking.marked?.role ?? current,
  );
  const [reason, setReason] = useState(marking.marked?.reason ?? "");
  const [refusal, setRefusal] = useState<string | null>(null);
  const save = (mark: ElementRoleRequest | null) => {
    setRefusal(null);
    marking
      .onMark(mark)
      .then(onClose, (failure: unknown) =>
        setRefusal(refusalDetail(failure, UNREACHED)),
      );
  };
  return (
    <DialogContent aria-describedby={sentenceId}>
      <div>
        <DialogTitle>Mark element {number}</DialogTitle>
        <DialogDescription id={sentenceId} className="mt-1.5">
          Choose whether an independent claim on this position needs the
          element, or whether a dependent claim can add it. Your choice saves
          even where the prior art answers it.
        </DialogDescription>
      </div>
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          save({ role, reason: reason.trim() });
        }}
      >
        <MarkFields
          role={role}
          reason={reason}
          onRole={setRole}
          onReason={setReason}
        />
        {refusal !== null && (
          <Note tone="destructive" role="alert">
            It could not be saved: {refusal}
          </Note>
        )}
        <DialogFooter>
          {marking.marked !== undefined && (
            <Button
              type="button"
              variant="quiet"
              disabled={marking.busy}
              onClick={() => save(null)}
            >
              Use the strip&apos;s reading
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={marking.busy || reason.trim() === ""}>
            Save
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

/**
 * The attorney's saved mark read as a strip entry, for an element no strip
 * holds: a position the run has not ruled, or one added after it. Where a strip
 * holds the element, the server has laid the mark over it already.
 */
function markedEntry(
  marked: ElementRoleResponse | undefined,
): StripEntryResponse | undefined {
  if (marked === undefined) return undefined;
  return {
    element_id: marked.element_id,
    role: marked.role,
    flagged: false,
    decided_by: "attorney",
    reason: `You marked it ${ROLE[marked.role].label.toLowerCase()}: ${marked.reason}`,
  };
}

/**
 * What the strip says of one element: essential, retreat, or not assessed,
 * with the server's reason on hover and on focus. A position the run has not
 * ruled, or an element the strip does not hold, reads not assessed, unless the
 * attorney marked it. Where the attorney may mark it, the mark holds the
 * control that does.
 */
export function StripMark({
  elementId,
  number,
  strip,
  marking,
}: {
  elementId: string;
  number: number;
  strip: StripResponse | null;
  marking?: ElementMarking;
}) {
  const [open, setOpen] = useState(false);
  const entry: StripEntryResponse | undefined =
    strip?.entries.find((candidate) => candidate.element_id === elementId) ??
    markedEntry(marking?.marked);
  const assessed =
    entry !== undefined &&
    (entry.decided_by === "attorney" || strip?.state !== "not_assessed");
  const label = assessed ? ROLE[entry.role].label : NOT_ASSESSED.label;
  const reason = entry?.reason ?? NOT_ASSESSED.reason;
  return (
    <>
      <HoverTip tone="dark" tip={<span>{reason}</span>}>
        {({ triggerProps }) => (
          <StatusPill
            {...triggerProps}
            tabIndex={0}
            tone={assessed ? ROLE[entry.role].tone : "neutral"}
            size="sm"
            plain
            data-testid={`strip-${elementId}`}
            data-role={assessed ? entry.role : "not_assessed"}
            data-decided-by={entry?.decided_by}
          >
            {label}
          </StatusPill>
        )}
      </HoverTip>
      {marking !== undefined && (
        <Dialog open={open} onOpenChange={setOpen}>
          <IconButton
            type="button"
            size="dense"
            variant="quiet"
            aria-label={`Mark element ${number}`}
            disabled={marking.busy}
            onClick={() => setOpen(true)}
          >
            <PencilLine aria-hidden />
          </IconButton>
          {open && (
            <MarkDialog
              number={number}
              current={entry?.role ?? "essential"}
              marking={marking}
              onClose={() => setOpen(false)}
            />
          )}
        </Dialog>
      )}
    </>
  );
}

/**
 * The mapped elements with their numbers and agreed wording, each marked with
 * what the strip says of it, and the notes.
 */
export function RestsOn({
  attorney,
  elements,
  strip,
  busy,
  onMark,
}: {
  attorney: PositionResponse | undefined;
  elements: readonly MappableElement[];
  strip: StripResponse | null;
  busy: boolean;
  onMark: (
    positionId: string,
    elementId: string,
    mark: ElementRoleRequest | null,
  ) => Promise<unknown>;
}) {
  const chips = mapped(attorney?.element_ids ?? [], elements);
  return (
    <>
      <div>
        <MicroLabel as="p" size="sm" className="mb-1">
          Rests on invention elements
        </MicroLabel>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((element) => (
            <ElementChip
              key={element.element_id}
              element={element}
              mark={
                <StripMark
                  elementId={element.element_id}
                  number={element.number}
                  strip={strip}
                  marking={
                    attorney === undefined
                      ? undefined
                      : {
                          marked: attorney.element_roles.find(
                            (mark) => mark.element_id === element.element_id,
                          ),
                          busy,
                          onMark: (mark) =>
                            onMark(
                              attorney.position_id,
                              element.element_id,
                              mark,
                            ),
                        }
                  }
                />
              }
            />
          ))}
          {chips.length === 0 && (
            <span className="text-[11.5px] text-muted-foreground">
              No elements mapped.
            </span>
          )}
        </div>
      </div>
      <div>
        <MicroLabel as="p" size="sm" className="mb-1">
          Additional notes
        </MicroLabel>
        <p className="text-[12px] text-muted-foreground">
          {attorney?.notes ?? "No notes recorded"}
        </p>
      </div>
    </>
  );
}
